import { yupResolver } from '@hookform/resolvers/yup';
import {
    ArrowSquareOut,
    CloudArrowUp,
    Key,
    Note,
    ShieldCheck
} from '@phosphor-icons/react';
import { Button, Label, TextInput, Tooltip } from 'flowbite-react';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { APP_PLATFORM_LOGO_MAP } from '../../../../shared/constants';
import type { GoogleIAPFormData } from '../interface';
import AnimatedModal from '../../../../components/AnimatedModal';

interface GoogleIAPModalProps {
    isOpen: boolean;
    onClose: () => void;
    isSubmitting: boolean;
    onSubmit: (formData: GoogleIAPFormData) => void;
}

// Validation schema
const getGoogleIAPSchema = (t: any) => yup.object().shape({
    service_account_json: yup
        .mixed()
        .required(t('configurations.integrations.googleIap.errors.jsonRequired')),
    email: yup
        .string()
        .required(t('configurations.integrations.googleIap.errors.emailRequired')),
    // .email('Must be a valid email')
    // .matches(
    //     /@.*\.iam\.gserviceaccount\.com$/,
    //     'Must be a valid Google service account email'
    // ),
    bucket_name: yup
        .string()
        .required(t('configurations.integrations.googleIap.errors.bucketRequired'))
        .test(
            'valid-bucket-format',
            'Must be a valid Cloud Storage URI like gs://{bucketname}/earning',
            (value) => {
                const trimmed = value?.trim();
                if (!trimmed) return false;

                // Expected format: gs://{bucketname}/earning[/...] or gs://{bucketname}/earnings[/...]
                // - bucketname: any non-empty segment not containing '/'
                // - allow extra path segments after '/earning' or '/earnings'
                const re = /^gs:\/\/[^/]+\/earnings?(?:\/.*)?$/;
                return re.test(trimmed);
            }
        ),
});

const GoogleIAPModal: React.FC<GoogleIAPModalProps> = ({
    isOpen,
    onClose,
    isSubmitting,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
        clearErrors,
    } = useForm({
        resolver: yupResolver(getGoogleIAPSchema(t)),
        defaultValues: {
            service_account_json: null as any,
            email: '',
            bucket_name: '',
        },
    });

    const handleClose = () => {
        reset();
        setSelectedFile(null);
        setFileError(null);
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        validateAndSetFile(file);
    };

    const validateAndSetFile = async (file?: File) => {
        setFileError(null);
        clearErrors('service_account_json');

        if (!file) {
            setFileError(t('configurations.integrations.googleIap.errors.jsonSelect'));
            return;
        }

        if (!file.name.endsWith('.json')) {
            setFileError(t('configurations.integrations.googleIap.errors.jsonInvalid'));
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setFileError(t('configurations.integrations.googleIap.errors.fileSize'));
            return;
        }

        setSelectedFile(file);
        setValue('service_account_json', file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        validateAndSetFile(file);
    };

    const onFormSubmit = (data: { email: string; bucket_name: string; service_account_json: any }) => {
        if (!selectedFile) {
            setFileError(t('configurations.integrations.googleIap.errors.jsonSelect'));
            return;
        }

        const dt = new DataTransfer();
        dt.items.add(selectedFile);

        let bucketName = data.bucket_name?.trim();
        const match = bucketName.match(/^gs:\/\/([^/]+)\/earnings?(?:\/.*)?$/);
        if (match?.[1]) bucketName = match[1];

        onSubmit({
            service_account_json: dt.files,
            email: data.email,
            bucket_name: bucketName,
        });
    };

    const externalLinks = {
        serviceAccounts: 'https://console.cloud.google.com/iam-admin/serviceaccounts',
        earningReports: 'https://play.google.com/console/u/1/developers',
        supportGuide: '/support#google',
    };

    const modalTitle = (
        <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 dark:bg-neutral-700 rounded-xl flex items-center justify-center flex-shrink-0">
                <img
                    src={APP_PLATFORM_LOGO_MAP.playStore}
                    alt="Google"
                    className="w-8 h-8"
                />
            </div>
            <div>
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('configurations.integrations.google_iap')}</h3>
                    <Tooltip content={t('configurations.integrations.viewGuide')} placement="right">
                        <Note size={20} weight="duotone" className='text-gray-500 dark:text-gray-400 cursor-pointer' onClick={() => window.open(externalLinks.supportGuide, '_blank')} />
                    </Tooltip>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <ShieldCheck size={13} className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {t('configurations.integrations.secureNoteIap')}
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <AnimatedModal show={isOpen} onClose={handleClose} size="lg" title={modalTitle}>
            <div className="relative">
                <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
                    {/* File Upload */}
                    <div>
                        <Label htmlFor="service_account_json" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-primary-600 dark:text-primary-400" />
                            {t('configurations.integrations.googleIap.serviceAccountKeyLabel')}
                        </Label>
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed ${fileError || errors.service_account_json ? 'border-red-500' : ''} rounded-xl px-4 py-2 text-center cursor-pointer transition-all ${isDragging
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : selectedFile
                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                    : 'border-gray-200 hover:border-gray-300 dark:border-neutral-600 dark:hover:border-neutral-500'
                                }`}
                        >
                            <CloudArrowUp
                                size={32}
                                className={`mx-auto mb-2 ${selectedFile ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'
                                    }`}
                            />
                            {selectedFile ? (
                                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                                    {selectedFile.name}
                                </p>
                            ) : (
                                <>
                                    <p className={`text-sm text-gray-600 dark:text-gray-400 ${fileError || errors.service_account_json ? 'text-red-500' : ''}`}>
                                        {t('configurations.integrations.dropFile', { ext: 'JSON' })}
                                    </p>
                                    <p className={`text-xs text-gray-400 dark:text-gray-500 mt-1 ${fileError || errors.service_account_json ? 'text-red-500' : ''}`}>
                                        {t("configurations.integrations.googleIap.serviceAccountKeyDescription")}
                                    </p>
                                </>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json,application/json"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </div>
                        {(fileError || errors.service_account_json) && (
                            <p className="text-sm text-red-600 mt-1">
                                {fileError || (errors.service_account_json?.message as string)}
                            </p>
                        )}
                    </div>

                    {/* Service Account Email */}
                    <div>
                        <Label htmlFor="email" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-primary-600 dark:text-primary-400" />
                            {t('configurations.integrations.googleIap.serviceAccountEmailLabel')}
                        </Label>
                        <TextInput
                            id="email"
                            placeholder="e.g. apprevbooster@project-id.iam.gserviceaccount.com"
                            {...register('email')}
                            color={errors.email ? 'failure' : undefined}
                        />
                        {errors.email && (
                            <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
                        )}
                        <a
                            href={externalLinks.serviceAccounts}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mt-1"
                        >
                            <ArrowSquareOut size={12} />
                            Open Google Cloud Console → IAM & Admin → Service Accounts
                        </a>
                    </div>

                    {/* Bucket Name */}
                    <div>
                        <Label htmlFor="bucket_name" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-primary-600 dark:text-primary-400" />
                            {t('configurations.integrations.googleIap.bucketNameLabel')}
                        </Label>
                        <TextInput
                            id="bucket_name"
                            placeholder="eg., gs://pubsite_prod_rev_05493956521912004335/earning"
                            {...register('bucket_name')}
                            color={errors.bucket_name ? 'failure' : undefined}
                        />
                        {errors.bucket_name && (
                            <p className="text-sm text-red-600 mt-1">{errors.bucket_name.message}</p>
                        )}
                        <a
                            href={externalLinks.earningReports}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mt-1"
                        >
                            <ArrowSquareOut size={12} />
                            Play Console → Download Reports → Financial → Earning Reports → "Copy Cloud Storage URI"
                        </a>
                    </div>

                    {/* Note Banner */}
                    <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-2 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                        <p className="font-semibold mb-1">{t('validation.importantNote')}</p>
                        {t('configurations.integrations.googleIapNote')}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-neutral-700">
                        <Button color="light" onClick={handleClose} disabled={isSubmitting}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                            isProcessing={isSubmitting}
                        >
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={16} />
                                {t('configurations.integrations.connectServiceAccount')}
                            </div>
                        </Button>
                    </div>
                </form>
            </div>
        </AnimatedModal>
    );
};

export default GoogleIAPModal;
